import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowRight, FiX } from 'react-icons/fi';

const businesses = [
  {
    id: 'consulting',
    name: 'EPOQ Consulting',
    desc: 'Strategic consulting for businesses navigating transformation, innovation and implementation. EPOQ Consulting helps turn complex challenges into practical action and long-term value.',
    img: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1000&auto=format&fit=crop',
    type: 'modal',
    modalTitle: 'EPOQ Consulting',
    modalBody: 'Strategic consulting for businesses navigating transformation, innovation and implementation. We work with organisations to turn complex challenges into practical action and long-term value.'
  },
  {
    id: 'mobility',
    name: 'EPOQ Mobility',
    desc: 'Exploring and developing practical applications for autonomous mobility and logistics. EPOQ Mobility connects emerging vehicle technology with real-world operational needs.',
    img: 'https://media-manager-c.questera.ai/greta-media/4948ae8bd3a71eaecd010e3b75a418c9afeda68ebd57283642e35d04cbb3fca3a3104393b4fae87688db585a45c44e03/images/aW1hZ2UvanBlZw==/cb69cb65c925d468167d3902c977065f.jpeg',
    type: 'link',
    link: 'https://epoqmobility.com/'
  },
  {
    id: 'capital',
    name: 'EPOQ Capital',
    desc: 'Exploring selected investment and business development opportunities with a long-term perspective. EPOQ Capital focuses on ventures where capital, commercial insight and strong partnerships can support sustainable growth.',
    img: 'https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=1000&auto=format&fit=crop',
    type: 'modal',
    modalTitle: 'EPOQ Capital',
    modalBody: 'Exploring selected investment and business development opportunities with a long-term perspective, bringing together capital, commercial insight and strong partnerships.'
  },
  {
    id: 'learn',
    name: 'EPOQ Learn',
    desc: 'Developing AI-ready talent for the changing world of work. EPOQ Learn combines structured learning, practical experience and real business assignments to build capable consultants for modern organisations.',
    img: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=1000&auto=format&fit=crop',
    type: 'link',
    link: 'https://epoqlearn.com/'
  }
];

const Structure = () => {
  const [selectedVenture, setSelectedVenture] = useState(null);
  const triggerRefs = useRef({});

  const closeModal = useCallback(() => {
    const lastSelected = selectedVenture;
    setSelectedVenture(null);
    // Return focus to the card that opened the modal
    if (lastSelected && triggerRefs.current[lastSelected.id]) {
      triggerRefs.current[lastSelected.id].focus();
    }
  }, [selectedVenture]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    if (selectedVenture) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [selectedVenture, closeModal]);

  return (
    <section id="businesses" className="py-24 md:py-48 bg-brand-primary relative">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-brand-secondary/40 to-transparent"></div>
      
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-4xl mb-32">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }} 
            transition={{ duration: 0.8 }} 
            className="font-display text-4xl md:text-6xl font-light text-white mb-10 tracking-tight leading-tight"
          >
            Four businesses.<br/>One shared direction.
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0 }} 
            whileInView={{ opacity: 1 }} 
            viewport={{ once: true }} 
            transition={{ duration: 0.8, delay: 0.2 }} 
            className="text-white/90 text-xl md:text-2xl font-light max-w-3xl leading-relaxed"
          >
            Each EPOQ business operates with its own focus, audience and expertise, connected by a common commitment to turning ambition into practical progress.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-10">
          {businesses.map((biz, index) => {
            const isModal = biz.type === 'modal';
            const cardProps = {
              key: biz.name,
              ref: (el) => (triggerRefs.current[biz.id] = el),
              initial: { opacity: 0, y: 30 },
              whileInView: { opacity: 1, y: 0 },
              viewport: { once: true },
              transition: { duration: 0.8, delay: index * 0.1 },
              className: "group relative h-[600px] md:h-[720px] overflow-hidden bg-brand-primary flex flex-col justify-end p-12 border border-brand-secondary/30 hover:border-brand-accent/50 transition-all duration-700 shadow-2xl cursor-pointer text-left w-full focus:outline-none focus:ring-2 focus:ring-brand-accent/50"
            };

            const cardContent = (
              <>
                <div className="absolute inset-0 z-0 overflow-hidden">
                  <div className="absolute inset-0 bg-brand-primary/30 group-hover:bg-brand-primary/10 transition-colors duration-1000 z-10"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-primary/90 via-brand-primary/10 to-transparent z-10"></div>
                  <img 
                    src={biz.img} 
                    alt={biz.name} 
                    className="w-full h-full object-cover opacity-80 group-hover:scale-110 group-hover:opacity-100 transition-all duration-[2000ms] ease-out saturate-[0.8]" 
                  />
                </div>
                <div className="relative z-20">
                  <h3 className="font-display text-3xl md:text-4xl text-white mb-6 tracking-wide leading-tight">{biz.name}</h3>
                  <p className="text-white/90 font-light mb-12 text-lg leading-relaxed opacity-100">{biz.desc}</p>
                  <div className="flex items-center text-[13px] tracking-[0.25em] uppercase text-white font-bold group-hover:text-brand-accent transition-colors">
                    <span className="mr-5 border-b-2 border-transparent group-hover:border-brand-accent pb-1 transition-all">
                      {isModal ? 'View Details' : 'Explore Brand'}
                    </span>
                    <FiArrowRight className="text-lg transform group-hover:translate-x-3 transition-transform duration-500" />
                  </div>
                </div>
              </>
            );

            return isModal ? (
              <motion.button 
                {...cardProps}
                onClick={() => setSelectedVenture(biz)}
              >
                {cardContent}
              </motion.button>
            ) : (
              <motion.a 
                {...cardProps}
                href={biz.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                {cardContent}
              </motion.a>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {selectedVenture && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 md:p-12">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="absolute inset-0 bg-brand-primary/90 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative w-full max-w-2xl bg-brand-primary border border-brand-secondary/40 p-10 md:p-16 shadow-[0_50px_100px_rgba(0,0,0,0.6)] z-10"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-title"
            >
              <button 
                onClick={closeModal}
                className="absolute top-8 right-8 text-white/50 hover:text-brand-accent transition-colors p-2"
                aria-label="Close modal"
              >
                <FiX size={28} />
              </button>
              
              <div className="space-y-8">
                <span className="text-brand-accent text-[11px] tracking-[0.4em] uppercase font-bold block">Venture Details</span>
                <h3 id="modal-title" className="font-display text-4xl md:text-5xl text-white font-light tracking-tight">
                  {selectedVenture.modalTitle}
                </h3>
                <p className="text-white/90 text-xl md:text-2xl font-light leading-relaxed">
                  {selectedVenture.modalBody}
                </p>
                <div className="pt-8">
                  <button 
                    onClick={closeModal}
                    className="text-[13px] tracking-[0.25em] uppercase text-white font-bold border-b-2 border-brand-secondary/40 hover:border-brand-accent hover:text-brand-accent transition-all pb-1"
                  >
                    Close Information
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Structure;